import { SubprogramService } from '@modules/subprogram/subprogram-service';
import Errors from '@shared/error/errors';
import { routerAsync } from '@/app-module';
import Message from '@shared/message/message';
import { i18n } from '@shared/i18n';
import { get, update} from 'lodash'

const stripePaymentFeesByCountry = require(`@enums/stripePaymentFeesByCountry.json`)

const PRICE_BY_100 = 100;
const feeContryType = { 
    EUROPEAN: 'EUROPEAN',
    NON_EUROPEAN: 'NON_EUROPEAN',
    COUNTRY: 'COUNTRY',
    INTERNATIONAL: 'INTERNATIONAL',
}

const configDefaultFee = {
    label: `${i18n( 'entities.subprogram.form.payment.fee.label')}`,
    iconClass: 'icon-tax'
}

const ConfigFeesInfo =  {
    EUROPEAN: {
        ...configDefaultFee,
        info: `${i18n('entities.subprogram.form.payment.fee.forEuropean')}`,
    },
    NON_EUROPEAN:{
        ...configDefaultFee,
        info: `${i18n('entities.subprogram.form.payment.fee.forNonEuropean')}`,
    },
    COUNTRY: {
        ...configDefaultFee,
        info: `${i18n('entities.subprogram.form.payment.fee.forCountry')}`,
    },
    INTERNATIONAL: {
        ...configDefaultFee,
        info: `${i18n('entities.subprogram.form.payment.fee.forInternational')}`,
    }
}

const ConfigAmountToReceiveInfo =  {
    EUROPEAN: {
        label: `${i18n('entities.subprogram.form.payment.toReceive.label')}`,
        info: `${i18n('entities.subprogram.form.payment.toReceive.forEuropean')}`,
    },
    NON_EUROPEAN:{
        label: `${i18n('entities.subprogram.form.payment.toReceive.label')}`,
        info: `${i18n('entities.subprogram.form.payment.toReceive.forNonEuropean')}`,
    },
    COUNTRY: {
        label: `${i18n('entities.subprogram.form.payment.toReceive.label')}`,
        info: `${i18n('entities.subprogram.form.payment.toReceive.forCountry')}`,
    },
    INTERNATIONAL: {
        label: `${i18n('entities.subprogram.form.payment.toReceive.label')}`,
        info: `${i18n('entities.subprogram.form.payment.toReceive.forInternational')}`,
    }
}

const textInfo = {
    draft: `${i18n('entities.subprogram.form.payment.infoText.draft')}`,
    noDraft: `${i18n('entities.subprogram.form.payment.infoText.noDraft')}`,
}

const defaultRecord = {
    status: 'draft',
}

export default {
    namespaced: true,
    
    state: {
        findLoading: false,
        owqloFeeLoading: false,
        saveLoading: false,
        newIdLoading: false,
        record: defaultRecord,
        isNewForm: false,
        owqloFeesArray:[],
        amountToReceiveArray:[],
        defaultCurrency: null,
        country: null
    },
    
    getters: {
        isNewForm: (state) => state.isNewForm,
        record: (state) => state.record,
        findLoading: (state) => !!state.findLoading,
        saveLoading: (state) => !!state.saveLoading,
        newIdLoading: (state) => !!state.newIdLoading,
        defaultCurrency: (state) => state.defaultCurrency,
        owqloFeeLoading: (state) => !!state.owqloFeeLoading,
        owqloFeesArray: (state) => state.owqloFeesArray,
        amountToReceiveArray: (state) => state.amountToReceiveArray,
        textInfo: (state) => state.record && state.record.status === 'draft' || state.isNewForm ? textInfo.draft : textInfo.noDraft,
    },
    
    mutations: {
        RESET(state) {
            state.findLoading = false;
            state.saveLoading = false;
            state.record = defaultRecord;
            state.defaultCurrency = null;
            state.isNewForm = true;
        },
        
        FIND_STARTED(state) {
            state.record = defaultRecord;
            state.findLoading = true;
        },
        
        FIND_SUCCESS(state, { record }) {
            state.record = record;
            state.isNewForm = false;
            state.findLoading = false;
        },
        
        FIND_ERROR(state) {
            state.record = defaultRecord;
            state.findLoading = false;
        },
        
        CREATE_STARTED(state) {
            state.saveLoading = true;
        },
        
        CREATE_SUCCESS(state) {
            state.saveLoading = false;
        },
        
        CREATE_ERROR(state) {
            state.saveLoading = false;
        },
        
        UPDATE_STARTED(state) {
            state.saveLoading = true;
        },
        
        UPDATE_SUCCESS(state) {
            state.saveLoading = false;
        },
        
        UPDATE_ERROR(state) {
            state.saveLoading = false;
        },
        
        UPDATE_STATUS_STARTED(state) {
            state.saveLoading = true;
        },
        
        UPDATE_STATUS_SUCCESS(state) {
            state.saveLoading = false;
        },
        
        UPDATE_STATUS_ERROR(state) {
            state.saveLoading = false;
        },

        NEW_ID_STARTED(state) {
            state.newIdLoading = true;
        },

        NEW_ID_SUCCESS(state) {
            state.newIdLoading = false;
        },
        
        NEW_ID_ERROR(state) {
            state.newIdLoading = false;
        },
        
        OWQLO_FEE_STARTED(state) {
            state.owqloFeeLoading = true;
        },
        
        OWQLO_FEE_SUCCESS(state, { owqloFeesArray, defaultCurrency }) {
            state.owqloFeeLoading = false;
            state.owqloFeesArray = owqloFeesArray;
            state.defaultCurrency = defaultCurrency;
        },
        
        OWQLO_FEE_ERROR(state) {
            state.owqloFeeLoading = false;
        },
        
        SET_AMOUNT_TO_RECEIVE(state, payload) {
            state.amountToReceiveArray = payload;
        },
    },
    
    actions: {
        doNew({ commit }) {
            commit('RESET');
        },
        
        async doGetNewId({ commit }, {subpropertyId, programId}) {
            commit('RESET');
            commit('NEW_ID_STARTED');
            try {
                const newId =  await SubprogramService.newId({subpropertyId, programId});
                commit('NEW_ID_SUCCESS');
                return newId;
            } catch (error) { 
                Errors.handle(error);
                commit('NEW_ID_ERROR');
            }
        },
        
        async doFind({ commit}, { id, propertyId, subpropertyId, programId }) {        
            try {
                commit('FIND_STARTED');
                const record = await SubprogramService.find(id, {type: 'program', propertyId, subpropertyId, programId});

                if (get(record, 'payment.price', false)) {
                    update(record, 'payment.price', (price) => price / PRICE_BY_100)
                }
                
                commit('FIND_SUCCESS', { record });

            } catch (error) {
                Errors.handle(error);
                commit('FIND_ERROR');
            }
        },
        
        async doCreate({ commit }, { id, values, propertyId, subpropertyId, programId }) {
            try {
                commit('CREATE_STARTED');
                
                if (get(values, 'payment.price', false)) {
                    update(values, 'payment.price', (price) => price * PRICE_BY_100)
                }

                await SubprogramService.create(id, values, {type: 'program', propertyId, subpropertyId, programId});

                commit('CREATE_SUCCESS');
                Message.success(i18n('entities.subprogram.create.success'));
                routerAsync().go(-1);
            } catch (error) {
                Errors.handle(error);
                commit('CREATE_ERROR');
                routerAsync().go(-1);
            }
        },

        async doUpdate({ commit }, { id, values, propertyId, subpropertyId, programId }) {
            try {
                commit('UPDATE_STARTED');

                // Si el payment existe y tiene precio se lo actualizamos
                if (get(values, 'payment.price', false)) {
                    update(values, 'payment.price', (price) => price * PRICE_BY_100)
                } else {
                    // Si no tiene payment con pago eliminamos el price de los values
                    delete values.payment.price;
                }

                await SubprogramService.update(id, values, {type: 'program', propertyId, subpropertyId, programId});

                commit('UPDATE_SUCCESS');
                Message.success(i18n('entities.subprogram.update.success'));

                routerAsync().go(-1);
            } catch (error) {
                Errors.handle(error);
                commit('UPDATE_ERROR');
                routerAsync().go(-1);
            }
        },

        async doUpdateStatus({ commit, dispatch, rootGetters }, { id, status, propertyId, subpropertyId, programId }) {
            try {

                await SubprogramService.changeStatus(id, status, { type: 'program', propertyId, subpropertyId, programId });

                commit('UPDATE_STATUS_SUCCESS');
                Message.success(i18n('entities.subprogram.update.success'));

                dispatch(
                    `${'program/list'}/doFetch`,
                    {
                        filter: rootGetters[`${'program/list'}/filter`],
                    },
                    {
                        root: true,
                    },
                );
            } catch (error) {
                Errors.handle(error);
                commit('UPDATE_STATUS_ERROR');
                routerAsync().go(-1);
            }
            
        },  

        async doLoadOwqloFee({ commit }, { subpropertyId }){
            
            try {
                commit('OWQLO_FEE_STARTED');

                const stripeData = await SubprogramService.stripeData({ subpropertyId });
                const amountOwqlo = get(stripeData, 'owqloFee.fixedAmount', 0);
                const percentageOwqlo = get(stripeData, 'owqloFee.percentage', 0);
                const defaultCurrency = get(stripeData, 'connectedAccount.defaultCurrency', null);
                const country = get(stripeData, 'connectedAccount.address.country', null);
                const stripeFeesByCountry = get(stripePaymentFeesByCountry, `${country}`, []);

                const owqloFeesArray = stripeFeesByCountry.map(({ type, amount, percentage }) => { 
                    let feeObject = {}

                    if (type === feeContryType.EUROPEAN) {
                        feeObject = Object.assign({}, ConfigFeesInfo.EUROPEAN);
                    } else if (type === feeContryType.NON_EUROPEAN) {
                        feeObject = Object.assign({}, ConfigFeesInfo.NON_EUROPEAN);
                    } else if (type === feeContryType.COUNTRY) {
                        feeObject = Object.assign({}, ConfigFeesInfo.COUNTRY);
                    } else if (type === feeContryType.INTERNATIONAL) {
                        feeObject = Object.assign({}, ConfigFeesInfo.INTERNATIONAL);
                    }

                    const amountTotal = (amount + amountOwqlo) / PRICE_BY_100;
                    const percentageTotal = percentage + percentageOwqlo;

                    update(feeObject, 'type', () => type);
                    update(feeObject, 'amount', () => amountTotal);
                    update(feeObject, 'currency', () => defaultCurrency);
                    update(feeObject, 'percentage', () => percentageTotal);

                    return feeObject;
                })

                commit('OWQLO_FEE_SUCCESS', { owqloFeesArray, defaultCurrency });            
            } catch (error) {
                commit('OWQLO_FEE_ERROR');                
            }
        },
        
        doCalculateAmountToReceive({ commit, getters }, { price }) {
            const amountToReceiveArray = getters.owqloFeesArray.map(({ amount, currency, percentage, type }) => { 

                const fee = ((price * percentage) / 100 + amount);
                const priceWithFee = price - fee;

                var feeObject = {}

                if (type === feeContryType.EUROPEAN) {
                    feeObject = Object.assign({}, ConfigAmountToReceiveInfo.EUROPEAN);
                } else if (type === feeContryType.NON_EUROPEAN) {
                    feeObject = Object.assign({}, ConfigAmountToReceiveInfo.NON_EUROPEAN);
                } else if (type === feeContryType.COUNTRY) {
                    feeObject = Object.assign({}, ConfigAmountToReceiveInfo.COUNTRY);
                } else if (type === feeContryType.INTERNATIONAL) {
                    feeObject = Object.assign({}, ConfigAmountToReceiveInfo.INTERNATIONAL);
                }
        
                update(feeObject, 'amount', () => priceWithFee);
                update(feeObject, 'currency', () => currency);

                // Validate
                if (priceWithFee <= 0) {
                    update(feeObject, 'textEmpty', () => '-');
                } else {
                    update(feeObject, 'textEmpty', () => null);
                }
                
                return feeObject;
            });
            
            commit('SET_AMOUNT_TO_RECEIVE', amountToReceiveArray);
        },
    }
  };